import { useMemo, useEffect, RefObject } from 'react';
import { Layer, Source, MapRef, SourceProps, LayerProps } from 'react-map-gl/maplibre';
import { useAtomValue } from 'jotai';

import { Coordinate } from '../../job-explorer.types';
import { getUserCoordinatesAtom } from '../../job-explorer-map.atoms';

function createSourceProps(userCoordinates?: Coordinate): SourceProps {
  return {
    id: 'dot-point',
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: userCoordinates?.lon && userCoordinates?.lat ? [userCoordinates.lon, userCoordinates.lat] : [],
          },
          properties: {},
        },
      ],
    },
  };
}

function createLayerProps(): LayerProps {
  return {
    id: 'layer-with-pulsing-dot',
    type: 'symbol',
    source: 'dot-point',
    layout: {
      'icon-image': 'pulsing-dot',
    },
  };
}

export function constructPulsingDot({ size = 100, map }: { size?: number; map: MapRef }) {
  return {
    width: size,
    height: size,
    data: new Uint8Array(size * size * 4),
    onAdd: function () {
      const canvas = document.createElement('canvas');
      canvas.width = this.width;
      canvas.height = this.height;
      this.context = canvas.getContext('2d');
    },
    render: function () {
      const duration = 1000;
      const t = (performance.now() % duration) / duration;
      const radius = (size / 2) * 0.3;
      const outerRadius = (size / 2) * 0.7 * t + radius;
      const context = this.context;

      // Draw the outer circle
      context.clearRect(0, 0, this.width, this.height);
      context.beginPath();
      context.arc(this.width / 2, this.height / 2, outerRadius, 0, Math.PI * 2);
      context.fillStyle = '#d4c1f5';
      context.fill();

      // Draw the inner circle
      context.beginPath();
      context.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2);
      context.fillStyle = '#693eb5';
      context.strokeStyle = '#693eb5';
      context.lineWidth = 2 + 4 * (1 - t);
      context.fill();
      context.stroke();

      // Update image data
      this.data = context.getImageData(0, 0, this.width, this.height).data;

      // Trigger map repaint for animation
      map?.triggerRepaint();

      return true;
    },
  };
}

export const usePulsingDot = ({ mapRef }: { mapRef: RefObject<MapRef | null> }) => {
  const userCoordinates = useAtomValue(getUserCoordinatesAtom);

  const map = mapRef?.current;

  const sourceProps: SourceProps = useMemo(() => createSourceProps(userCoordinates), [userCoordinates]);

  const layerProps: LayerProps = useMemo(() => createLayerProps(), []);

  const pulsingDot = useMemo(() => constructPulsingDot({ map }), [map]);

  useEffect(() => {
    if (!map) return;

    const addPulsingDot = () => {
      // Guard against re-adding (MapLibre throws on a duplicate image id).
      if (!map.hasImage('pulsing-dot')) {
        map.addImage('pulsing-dot', pulsingDot, { pixelRatio: 2 });
      }
    };

    // The `layer-with-pulsing-dot` layer references `icon-image: 'pulsing-dot'`. If the image is
    // not registered by the time MapLibre renders the layer it logs "Image 'pulsing-dot' could not
    // be loaded". Register on demand via `styleimagemissing` (fires exactly when the layer needs
    // it, and again after any style reload), plus once now if the style is already loaded.
    const handleStyleImageMissing = (e: { id: string }) => {
      if (e.id === 'pulsing-dot') addPulsingDot();
    };
    map.on('styleimagemissing', handleStyleImageMissing);
    if (map.isStyleLoaded?.()) addPulsingDot();

    return () => {
      map.off('styleimagemissing', handleStyleImageMissing);
    };
  }, [map, pulsingDot]);

  return (
    <Source {...sourceProps}>
      <Layer {...layerProps} />
    </Source>
  );
};
